import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { Col, Form, Row } from 'react-bootstrap';
import { useToast } from '../../../context/Toast';
import { UserRole } from '../../../services/utils/enum';
import {
  StyledInputCellphoneAddUser,
  StyledInputTextModalUser,
  StyledModal,
  StyledSelectModalUser,
  birthDatePickerTextFieldSx,
} from '../../../pages/settings/modalUser/styles';
import { LabelNames } from '../../../pages/userRegister/style';
import { ButtonAddGroup, StyledInputText, StyledModalFooterAddMemberOnGroup } from '../../../pages/settings/styles';
import { Button } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import { patchUser } from '../../../services/user/user';
import { browserSession } from '../../../services/utils/browserSession';
import { getApiErrorMessage } from '../../../services/utils/apiError';
import {
  buildUnexpectedUserFormErrorMessage,
  ConflictField,
  getConflictValidation,
} from '../../../pages/settings/userFormErrors';
import { useTranslation } from 'react-i18next';
import { getCountryOptions, resolveCountryCode } from '../../../i18n/country';
import { getLocalizedPhoneLabels } from '../../../i18n/phoneLabels';
import { useAppLocale } from '../../../i18n/useAppLocale';
import {
  birthDatePickerCalendarSx,
  getBirthDateDayOfWeekFormatter,
  getBirthDateFormat,
} from '../../../i18n/birthDate';

interface IFormState {
  first_name: string;
  last_name: string;
  email: string;
  birth_date: string;
  country?: string;
  cellphone?: string;
  role: number;
  sex?: number;
  id: string;
}

const emptyFormState: IFormState = {
  first_name: '',
  last_name: '',
  email: '',
  birth_date: '',
  country: '',
  cellphone: '',
  role: UserRole.employee,
  sex: 3,
  id: '',
};

const buildFormState = (userInfo: IFormState | null): IFormState => ({
  ...emptyFormState,
  ...(userInfo ?? {}),
  first_name: userInfo?.first_name ?? '',
  last_name: userInfo?.last_name ?? '',
  email: userInfo?.email ?? '',
  birth_date: userInfo?.birth_date ?? '',
  country: resolveCountryCode(userInfo?.country),
  cellphone: userInfo?.cellphone ?? '',
  role: userInfo?.role ?? UserRole.employee,
  sex: userInfo?.sex ?? 3,
  id: userInfo?.id ?? '',
});

const normalizeText = (value?: string | null) => (value ?? '').trim();

interface ModalEditUserProps {
  show: boolean;
  onClose: () => void;
  userInfo: IFormState | null;
}

function ModalEditUser({ show, onClose, userInfo }: ModalEditUserProps) {
  const { t } = useTranslation('settings');
  const appLocale = useAppLocale();
  const { showToast } = useToast();
  const [showLoading, setShowLoading] = useState(false);
  const { user, SignOut } = useAuth();
  const informedRequesterId = browserSession.getIdentity()?.user?.id ?? user?.user?.id;
  const [form, setForm] = useState<IFormState>(() => ({ ...buildFormState(userInfo), country: resolveCountryCode(userInfo?.country) }));
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ConflictField, string>>>({});

  useEffect(() => {
    if (!show) {
      setFieldErrors({});
      setShowLoading(false);
      return;
    }

    setForm({ ...buildFormState(userInfo), country: resolveCountryCode(userInfo?.country) });
    setFieldErrors({});
  }, [show, userInfo]);

  const resetState = () => {
    setForm({ ...buildFormState(userInfo), country: resolveCountryCode(userInfo?.country) });
    setFieldErrors({});
    setShowLoading(false);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const clearFieldError = (field: ConflictField) => {
    setFieldErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = event.target;

    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));

    if (name === 'email') {
      clearFieldError('email');
    }
  };

  const handlePhoneChange = (value?: string) => {
    setForm((prevForm) => ({
      ...prevForm,
      cellphone: value || '',
    }));

    clearFieldError('cellphone');
  };

  const handleBirthDateChange = (value: Dayjs | null) => {
    setForm((current) => ({
      ...current,
      birth_date: value?.isValid() ? value.format('YYYY-MM-DD') : '',
    }));
  };

  const isFormUnchanged = () => {
    const initialForm = buildFormState(userInfo);

    return (
      normalizeText(form.first_name) === normalizeText(initialForm.first_name) &&
      normalizeText(form.last_name) === normalizeText(initialForm.last_name) &&
      normalizeText(form.email) === normalizeText(initialForm.email) &&
      form.birth_date === initialForm.birth_date &&
      (form.country ?? '') === (initialForm.country ?? '') &&
      normalizeText(form.cellphone) === normalizeText(initialForm.cellphone) &&
      Number(form.sex ?? 3) === Number(initialForm.sex ?? 3)
    );
  };

  const buildPatchPayload = () => {
    const initialForm = buildFormState(userInfo);
    const payload: any = {
      company_id: user.company_visited?.id,
      id: userInfo?.id,
      informedRequesterId,
    };

    if (normalizeText(form.first_name) !== normalizeText(initialForm.first_name)) {
      payload.first_name = normalizeText(form.first_name);
    }

    if (normalizeText(form.last_name) !== normalizeText(initialForm.last_name)) {
      payload.last_name = normalizeText(form.last_name);
    }

    if (normalizeText(form.email) !== normalizeText(initialForm.email)) {
      payload.email = normalizeText(form.email);
    }

    if (form.birth_date !== initialForm.birth_date) {
      payload.birth_date = form.birth_date;
    }

    if ((form.country ?? '') !== (initialForm.country ?? '')) {
      payload.country = resolveCountryCode(form.country);
    }

    if (Number(form.sex ?? 3) !== Number(initialForm.sex ?? 3)) {
      payload.sex = Number(form.sex);
    }

    const normalizedCellphone = normalizeText(form.cellphone);
    const initialCellphone = normalizeText(initialForm.cellphone);

    if (normalizedCellphone !== initialCellphone) {
      payload.cellphone = normalizedCellphone ? normalizedCellphone : null;
    }

    return payload;
  };

  const onSubmit = async () => {
    if (isFormUnchanged()) {
      handleClose();
      return;
    }

    const company = user.company_visited?.id;
    setShowLoading(true);

    if (!company) {
      showToast(t('users.sessionExpired'), 'info');
      setTimeout(() => {
        showToast(t('users.redirecting'), 'info');
        setTimeout(() => SignOut('/admin/signup'), 5000);
      }, 2500);
      setShowLoading(false);
      return;
    }

    try {
      const dataToSend = buildPatchPayload();
      dataToSend.company_id = company;

      await patchUser(dataToSend);
      showToast(t('users.updated'), 'success');
      handleClose();
    } catch (error: any) {
      const conflictValidation = getConflictValidation(error);

      if (conflictValidation) {
        setFieldErrors(conflictValidation.nextFieldErrors);
        showToast(conflictValidation.toastMessage, 'warning', 8000);
        return;
      }

      console.error(error);
      showToast(
        getApiErrorMessage(
          error,
          buildUnexpectedUserFormErrorMessage('atualizar as informações do usuário')
        ),
        'error',
        8000
      );
      throw error;
    } finally {
      setShowLoading(false);
    }
  };

  if (!show) return null;

  return (
    <StyledModal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header>
        <Modal.Title style={{ color: '#3C4043' }}>{t('users.editTitle')}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="first_name">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.firstName')}</LabelNames>
              <StyledInputText
                style={{ background: '#F1F1F1', paddingRight: 10 }}
                type="text"
                name="first_name"
                value={form.first_name}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="last_name">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.lastName')}</LabelNames>
              <StyledInputText
                style={{ background: '#F1F1F1', paddingRight: 10 }}
                type="text"
                name="last_name"
                value={form.last_name}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="email">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.email')}</LabelNames>
              <StyledInputTextModalUser
                $hasError={!!fieldErrors.email}
                style={{ background: '#F1F1F1', paddingRight: 10 }}
                type="email"
                name="email"
                value={form.email}
                aria-invalid={!!fieldErrors.email}
                onChange={handleInputChange}
              />
              {fieldErrors.email && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  {fieldErrors.email}
                </div>
              )}
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="country">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.country')}</LabelNames>
              <StyledSelectModalUser
                style={{ background: '#F1F1F1' }}
                name="country"
                value={form.country ?? ''}
                onChange={handleInputChange}
              >
                <option value="" disabled hidden>
                  {t('users.select')}
                </option>

                {getCountryOptions(appLocale).map((country) => (
                  <option key={country.value} value={country.value}>
                    {country.label}
                  </option>
                ))}
              </StyledSelectModalUser>
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="birth_date">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.birthDate')}</LabelNames>
              <DatePicker
                value={form.birth_date ? dayjs(form.birth_date) : null}
                onChange={handleBirthDateChange}
                format={getBirthDateFormat(appLocale)}
                dayOfWeekFormatter={getBirthDateDayOfWeekFormatter(appLocale)}
                disabled={showLoading}
                minDate={dayjs('1925-01-01')}
                maxDate={dayjs()}
                fixedWeekNumber={6}
                reduceAnimations
                slotProps={{
                  textField: {
                    fullWidth: true,
                    sx: birthDatePickerTextFieldSx,
                  },
                  layout: { sx: birthDatePickerCalendarSx },
                }}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="cellphone">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.phone')}</LabelNames>
              <StyledInputCellphoneAddUser
                $hasError={!!fieldErrors.cellphone}
                name="cellphone"
                value={form.cellphone ?? ''}
                onChange={handlePhoneChange}
                limitMaxLength={true}
                labels={getLocalizedPhoneLabels(appLocale)}
                defaultCountry="BR"
                className="input-admin"
              />
              {fieldErrors.cellphone && (
                <div style={{ color: '#dc3545', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                  {fieldErrors.cellphone}
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col xs={12} sm={6}>
            <Form.Group className="mb-3" controlId="sex">
              <LabelNames style={{ color: '#3C4043' }}>{t('users.sex')}</LabelNames>
              <StyledSelectModalUser
                style={{ background: '#F1F1F1' }}
                name="sex"
                value={form.sex ?? 3}
                onChange={handleInputChange}
              >
                <option value={1}>{t('modal.male')}</option>
                <option value={2}>{t('modal.female')}</option>
                <option value={3}>{t('users.notInformed')}</option>
              </StyledSelectModalUser>
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <StyledModalFooterAddMemberOnGroup>
        <span style={{ color: '#A5A5AA', fontSize: '14px', textAlign: 'left' }}>
          {t('groups.saveHint')}
        </span>

        <ButtonAddGroup>
          <Button
            sx={{
              textTransform: 'capitalize',
              padding: '0.5em 1.5em',
              borderRadius: '2em',
              fontSize: '1em',
              fontWeight: 500,
              border: '1px solid #a0a0a0',
            }}
            variant="text"
            onClick={handleClose}
            disabled={showLoading}
          >
            {t('users.close')}
          </Button>
          <Button
            sx={{
              textTransform: 'capitalize',
              background: '#2491FF',
              borderRadius: '2em',
              fontSize: '1em',
              fontWeight: 'semibold ',
              padding: '0.5em 2em',
            }}
            variant="contained"
            onClick={() => {
              void onSubmit().catch(() => undefined);
            }}
            disabled={showLoading}
          >
            {showLoading ? t('users.saving') : t('users.save')}
          </Button>
        </ButtonAddGroup>
      </StyledModalFooterAddMemberOnGroup>
    </StyledModal>
  );
}

export default ModalEditUser;
